import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';

import AddMeal from './AddMeal';
import {
  isEnableLoadingSelector,
  mealListSelector,
} from './selector';
import { deleteMeal, getMealList, refreshMealList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { MealJson } from '@/types/domain/meal/entities/mealEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import MealInfo from '@/domain/meal/component/MealInfo';
import { toStringDateTimeDisplay } from '@/utils/date';

/** プロパティ */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const Meal: React.FC<Props> = ({ user, child, facilityList }) => {
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const mealList = useSelector(mealListSelector);
  const [
    deleteMealJson,
    setDeleteMealJson,
  ] = React.useState<MealJson | undefined>(undefined);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < mealList.length; i += 1) {
    if (mealList[i].data.childId === child.childId) {
      rows.push({
        id: i.toString(),
        measureDateTime:
          toStringDateTimeDisplay(new Date(mealList[i].data.measureDateTime), false),
        meal: mealList[i].data.mealContent,
        type: mealList[i].data.measurerType,
      });
    }
  }
  const columns: TableBaseColumnType[] = [
    { id: 'measureDateTime', label: '日時', minWidth: 110 },
    { id: 'meal', label: '内容', minWidth: 60 },
  ];
  const actions = [
    {
      icon: <DeleteIcon />,
      title: '削除',
      action: (r: TableBaseRowType) => {
        const item = mealList[parseInt(r.id, 10)];
        setDeleteMealJson(item);
      },
      disabled: (r: TableBaseRowType) => r.type !== 'app',
    },
  ];
  return (
    <div>
      <div>
        <TableBase
          rows={rows}
          columns={columns}
          actions={actions}
          addAction={() => setIsOpen(true)}
          refreshAction={() => dispatch(refreshMealList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getMealList())}
        />
        <AddMeal
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          child={child}
          user={user}
          facilityList={facilityList}
        />
        <Delete<MealJson>
          comp={MealInfo}
          data={deleteMealJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteMeal({
                data: json,
                facilityIds: facilityList.map((v) => v.data.facilityId),
              }));
            }
            setDeleteMealJson(undefined);
          }}
          title="食事"
          content="以下の記録を削除しますか？"
        />
      </div>
    </div>
  );
};

export default Meal;
